'use client';

import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn, Select, Logo } from '@mpange/ui';
import { NAV_SECTIONS } from '@/lib/nav';
import { canAccessRoute } from '@/lib/permissions';
import type { Organization } from '@/lib/api';

function isActive(pathname: string, href: string) {
  if (href === '/dashboard') return pathname === '/dashboard';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function AccountCard({ email, orgName }: { email: string; orgName?: string }) {
  return (
    <div className="rounded-lg border border-sidebar-border bg-white/5 px-3 py-2.5">
      <p className="truncate text-[12.5px] font-semibold text-sidebar-fg">{orgName ?? 'Organization'}</p>
      <p className="mt-0.5 truncate text-[11.5px] text-sidebar-muted">{email}</p>
    </div>
  );
}

export function Sidebar({
  userEmail,
  userIsStaff,
  organizations,
  currentOrgId,
  onOrgChange,
  mobileOpen,
  onClose,
}: {
  userEmail: string;
  userIsStaff?: boolean;
  organizations: Organization[];
  currentOrgId: string | null;
  onOrgChange: (orgId: string) => void;
  mobileOpen?: boolean;
  onClose?: () => void;
}) {
  const { pathname } = useLocation();
  const currentOrg = organizations.find((o) => o.id === currentOrgId) ?? organizations[0];

  const content = (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-fg">
      <div className="flex h-16 items-center gap-2.5 border-b border-sidebar-border px-5">
        <Logo variant="mark" size="sm" />
        <div className="leading-none">
          <p className="text-[15px] font-bold tracking-tight text-sidebar-fg">MPANGE</p>
          <p className="mt-0.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-brand">
            Communications
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md p-1.5 text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg md:hidden"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {organizations.length > 0 && (
        <div className="border-b border-sidebar-border px-3 py-3">
          {organizations.length > 1 ? (
            <Select
              value={currentOrgId ?? ''}
              onChange={(e) => onOrgChange(e.target.value)}
              className="border-sidebar-border bg-white/5 text-sm text-sidebar-fg"
            >
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName ?? o.name}
                </option>
              ))}
            </Select>
          ) : (
            <p className="truncate px-1 text-xs font-medium text-sidebar-fg">
              {currentOrg?.displayName ?? currentOrg?.name}
            </p>
          )}
        </div>
      )}

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4">
        {NAV_SECTIONS.map((group) => {
          const items = group.items.filter((item) =>
            canAccessRoute(currentOrg?.role, item.href),
          );
          if (items.length === 0) return null;
          return (
          <div key={group.section}>
            <p className="px-3 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
              {group.section}
            </p>
            <ul className="space-y-0.5">
              {items.map((item) => {
                const active = isActive(pathname, item.href);
                const Icon = item.icon;
                return (
                  <li key={`${group.section}-${item.href}`}>
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={cn(
                        'group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors',
                        active
                          ? 'bg-sidebar-active text-brand shadow-[inset_3px_0_0_0_var(--color-brand)]'
                          : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg',
                      )}
                      aria-current={active ? 'page' : undefined}
                    >
                      <Icon
                        className={cn(
                          'h-[18px] w-[18px]',
                          active ? 'text-brand' : 'text-sidebar-muted group-hover:text-sidebar-fg',
                        )}
                      />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          );
        })}
      </nav>

      <div className="space-y-2 border-t border-sidebar-border px-3 py-3">
        <AccountCard email={userEmail} orgName={currentOrg?.displayName ?? currentOrg?.name} />
        {userIsStaff && (
          <Link
            to="/admin"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium text-brand hover:bg-sidebar-active"
          >
            Admin console
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden w-64 shrink-0 md:block">{content}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 animate-fade-in bg-brand-navy/60"
            aria-label="Close navigation"
            onClick={onClose}
          />
          <div className="absolute left-0 top-0 h-full w-72 animate-slide-in-right shadow-modal">
            {content}
          </div>
        </div>
      )}
    </>
  );
}
